import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  IconButton, 
  Badge, 
  Menu, 
  Typography, 
  Box, 
  Divider, 
  List, 
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Button,
  Tooltip,
  alpha,
  Chip
} from '@mui/material';
import { 
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  Check as CheckIcon,
  ClearAll as ClearAllIcon,
  LockOpen as LockOpenIcon,
  Schedule as ScheduleIcon,
  DoneAll as DoneAllIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import notificationService from '../services/notificationService';
import { useNotification } from '../context/NotificationContext';

/**
 * Real-time Notification Bell Component
 * Displays notification count and dropdown with real-time updates
 */
const NotificationBell = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const unsubscribeRef = useRef(null);
  const { showSuccess, showError } = useNotification();

  const isMenuOpen = Boolean(anchorEl);

  /**
   * Handle real-time notification updates
   * @param {Array} newNotifications - Updated notifications
   */
  const handleNotificationUpdate = useCallback((newNotifications) => {
    setNotifications(newNotifications);
    setUnreadCount(newNotifications.length);
    
    // Trigger animation for new notifications
    if (newNotifications.length > 0) {
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 1000);
      
      // Show success message for new unlock request notifications
      const unlockNotifications = newNotifications.filter(
        notif => notif.entity_type === 'UnlockRequest'
      );
      
      if (unlockNotifications.length > 0) {
        showSuccess(`${unlockNotifications.length} new unlock request notification(s)`);
      }
    }
  }, [showSuccess]);

  /**
   * Load notifications manually
   */
  const loadNotifications = useCallback(async () => {
    try {
      await notificationService.checkForUpdates();
    } catch (error) {
      console.error('Error loading notifications:', error);
      showError('Failed to load notifications');
    }
  }, [showError]);

  useEffect(() => {
    // Monitor online status
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Subscribe to real-time notifications
    unsubscribeRef.current = notificationService.subscribe(handleNotificationUpdate);

    // Initial load
    loadNotifications();

    return () => {
      // Cleanup
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [handleNotificationUpdate, loadNotifications]);

  /**
   * Handle bell click
   */
  const handleBellClick = (event) => {
    setAnchorEl(event.currentTarget);
    // Refresh notifications when opening menu
    loadNotifications();
  };

  /**
   * Handle menu close
   */
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  /**
   * Mark notification as read
   */
  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      showSuccess('Notification marked as read');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      showError('Failed to mark notification as read');
    }
  };

  /**
   * Mark all notifications as read
   */
  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      showSuccess('All notifications marked as read');
      setAnchorEl(null);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      showError('Failed to mark all notifications as read');
    }
  };

  /**
   * Get notification icon based on type
   */
  const getNotificationIcon = (notification) => {
    if (notification.entity_type === 'UnlockRequest') {
      if (notification.title.includes('Approved')) {
        return <CheckIcon color="success" />;
      } else if (notification.title.includes('Rejected')) {
        return <ClearAllIcon color="error" />;
      } else {
        return <LockOpenIcon color="warning" />;
      }
    }
    return <ScheduleIcon />;
  };

  /**
   * Format notification time
   */
  const formatNotificationTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / 1000 / 60);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  /**
   * Navigate to related page
   */
  const handleNotificationClick = (notification) => {
    if (notification.entity_type === 'UnlockRequest') {
      // Navigate to unlock requests in settings page
      window.location.href = '/settings?tab=unlock-requests';
    }
    handleMarkAsRead(notification.id);
    handleMenuClose();
  };

  return (
    <>
      <Tooltip 
        title={isOnline 
          ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
          : 'Offline - notifications unavailable'
        }
      >
        <IconButton 
          onClick={handleBellClick}
          sx={{ 
            color: 'inherit',
            '&:hover': {
              backgroundColor: alpha('#fff', 0.1),
            }
          }}
        >
          <Badge 
            badgeContent={unreadCount} 
            color="error"
            invisible={unreadCount === 0}
            sx={{
              '& .MuiBadge-badge': {
                animation: isAnimating ? 'pulse 1s ease-in-out' : 'none',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.2)' },
                  '100%': { transform: 'scale(1)' }
                }
              }
            }}
          >
            <motion.div
              animate={isAnimating ? { 
                rotate: [0, -15, 15, -10, 10, -5, 5, 0],
                transition: { duration: 0.6 }
              } : {}}
            >
              {isAnimating || unreadCount > 0 ? 
                <NotificationsActiveIcon /> : 
                <NotificationsIcon />
              }
            </motion.div>
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 500,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {/* Header */}
        <Box sx={{ 
          p: 2, 
          borderBottom: 1, 
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" component="div">
            Notifications
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {!isOnline && (
              <Chip 
                label="Offline" 
                size="small" 
                color="error" 
                variant="outlined" 
              />
            )}
            {notifications.length > 0 && (
              <Button
                size="small"
                startIcon={<DoneAllIcon />}
                onClick={handleMarkAllAsRead}
                sx={{ minWidth: 'auto' }}
              >
                Mark All Read
              </Button>
            )}
          </Box>
        </Box>

        {/* Notifications List */}
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {notifications.length === 0 ? (
            <Box sx={{ 
              p: 3, 
              textAlign: 'center',
              color: 'text.secondary'
            }}>
              <NotificationsIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
              <Typography variant="body1">
                No new notifications
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {isOnline ? 
                  'You\'re all caught up!' : 
                  'Connect to internet to check for updates'
                }
              </Typography>
            </Box>
          ) : (
            <List sx={{ py: 0 }}>
              <AnimatePresence>
                {notifications.map((notification, index) => (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <ListItemButton
                      onClick={() => handleNotificationClick(notification)}
                      sx={{ 
                        borderBottom: 1, 
                        borderColor: 'divider',
                        '&:hover': {
                          backgroundColor: alpha('#1976d2', 0.04)
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {getNotificationIcon(notification)}
                      </ListItemIcon>
                      <ListItemText
                        primary={notification.title}
                        secondary={
                          <Box component="div">
                            <Box 
                              component="div"
                              sx={{ 
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                mb: 0.5,
                                fontSize: '0.875rem'
                              }}
                            >
                              {notification.message}
                            </Box>
                            <Box component="div" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              {formatNotificationTime(notification.created_at)}
                            </Box>
                          </Box>
                        }
                        primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 600 }}
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItemButton>
                  </motion.div>
                ))}
              </AnimatePresence>
            </List>
          )}
        </Box>

        {/* Footer */}
        {notifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1 }}>
              <Button
                fullWidth
                variant="text"
                onClick={() => {
                  window.location.href = '/settings?tab=unlock-requests';
                  handleMenuClose();
                }}
                sx={{ textTransform: 'none' }}
              >
                View All Unlock Requests
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell; 